import { Gear } from "@phosphor-icons/react";
import { isAfter, isBefore } from "date-fns";
import { TKeys } from "i18next";
import { useEffect, useMemo, useState } from "react";
import tw, { styled } from "twin.macro";
import "styled-components/macro";

import { Link } from "appRouting";
import { SchemaForm } from "common/form";
import {
  CustomRenderFields,
  FieldBoxVariantsProp,
  TextLabel,
  TextPlaceholder,
  customRender,
  fieldBoxStyle,
  fieldBoxVariants,
  fieldWrapperStyle,
} from "common/form/renderFields";
import { Button } from "common/guideline/components/Button";
import { Popover } from "common/guideline/components/Popover";
import { Text } from "common/guideline/components/Text";
import { applyDayBreak, dateToISOLocal, getDate, getDatesByDuration } from "common/helpers";
import type { DateRangeFormatted, Duration } from "common/helpers/reportingPeriods";
import { getLocalStorageData } from "common/hooks";
import { useTenant } from "tenant/context";
import { timeSettings } from "user/helpers/timeSettings";

const durationOptions: Array<{ value: Duration; label: TKeys }> = [
  { value: "hour", label: "common.table.filter.pastHour" },
  { value: "day", label: "common.table.filter.pastDay" },
  { value: "7days", label: "common.table.filter.past7Days" },
  { value: "30days", label: "common.table.filter.past30Days" },
  { value: "currentDay", label: "common.table.filter.currentDay" },
  { value: "currentWeek", label: "common.table.filter.currentWeek" },
  { value: "currentMonth", label: "common.table.filter.currentMonth" },
  { value: "lastDay", label: "common.table.filter.previousDay" },
  { value: "lastWeek", label: "common.table.filter.previousWeek" },
  { value: "lastMonth", label: "common.table.filter.previousMonth" },
  { value: "custom", label: "common.table.filter.customRange" },
];

const getDatesDurationByISO = ({ from, to, duration }: { from: Date; to: Date; duration: Duration }) => ({
  from: dateToISOLocal(from),
  to: dateToISOLocal(to),
  duration,
});

// we need to update line below, once new solution will be applied to all reports
const defaultOptions: Duration[] = ["day", "7days", "30days", "custom"];

const Wrapper = styled.div`
  ${tw`space-y-2 py-2`}
`;

const Box = styled.div<FieldBoxVariantsProp>`
  ${fieldBoxStyle}
  ${fieldBoxVariants}
  ${tw`flex justify-between items-center`}
`;

const getFields: (
  allowSingleDate?: boolean,
  fromDate?: Date,
  toDate?: Date,
  availableOptions?: Duration[],
) => CustomRenderFields[] = (allowSingleDate, fromDate, toDate, availableOptions = defaultOptions) => [
  {
    type: "radio",
    name: "duration",
    values: availableOptions ? durationOptions.filter((o) => availableOptions.includes(o.value)) : durationOptions,
    variant: "sm",
    calculation: {
      updates: (v: Duration) => (v === "custom" ? {} : getDatesByDuration[v]()),
    },
  },
  {
    type: "condition",
    when: "duration",
    is: (value) => value === "custom",
    fields: [
      {
        type: "container",
        Component: Wrapper,
        fields: [
          {
            type: "date",
            name: "from",
            label: "From",
            dateFormat: "datetime",
            fromDate,
            toDate,
            parse: (v) => (v ? dateToISOLocal(v) : v),
            format: (v) => (v ? new Date(v) : v),
            validate: (value, formData) =>
              !value
                ? allowSingleDate
                  ? null
                  : "common.table.filter.cannotBeEmpty"
                : formData.values.to && isAfter(new Date(value), new Date(formData.values.to))
                ? "common.table.filter.lowerTo"
                : null,
          },
          {
            type: "date",
            name: "to",
            label: "To",
            dateFormat: "datetime",
            fromDate,
            toDate,
            parse: (v) => (v ? dateToISOLocal(v) : v),
            format: (v) => (v ? new Date(v) : v),
            validate: (value, formData) =>
              !value
                ? allowSingleDate
                  ? null
                  : "common.table.filter.cannotBeEmpty"
                : formData.values.from && isBefore(new Date(value), new Date(formData.values.from))
                ? "common.table.filter.higherFrom"
                : null,
          },
        ],
      },
    ],
  },
];

const userDayEndOrTenantEndOfDay = (tenantEndOfDay) =>
  timeSettings.get.dayEndTime() != timeSettings.default.dayEnd
    ? timeSettings.get.dayEndTime()
    : tenantEndOfDay ?? timeSettings.get.dayEndTime();

type Props = FieldBoxVariantsProp & {
  onSubmit: (range: DateRangeFormatted) => void;
  value: DateRangeFormatted;
  label?: string;
  placeholder?: TKeys;
  isClearable?: boolean;
  allowSingleDate?: boolean;
  fromDate?: Date;
  toDate?: Date;
  availableOptions?: Duration[];
  offset?: string;
  firstDayOfTheWeek?: "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";
  useTenantEndOfDay?: boolean;
};

const DateFilter: React.FC<Props> = ({
  onSubmit,
  value,
  label,
  placeholder,
  isClearable,
  allowSingleDate,
  variant,
  fromDate,
  toDate,
  availableOptions,
  useTenantEndOfDay,
}) => {
  const options = availableOptions?.join("_");
  const fields = useMemo(
    () => getFields(allowSingleDate, fromDate, toDate, options?.split("_") as Duration[]),
    [allowSingleDate, fromDate, toDate, options],
  );

  const tenantEndOfDay = useTenant((state) => state.tenant?.endOfDay);

  return (
    <Popover
      auto
      overflowContainer
      tw="max-w-full"
      content={(close) => (
        <div tw="min-w-[200px]">
          <div tw="flex items-center justify-between">
            <div tw="flex flex-col">
              <Text
                variant="label"
                tKey="common.table.dayEndsAt"
                tValue={{
                  value: userDayEndOrTenantEndOfDay(tenantEndOfDay),
                }}
              />
              <Text
                variant="label"
                tKey="common.table.weekStartsAt"
                tValue={{ value: `$t(user.settings.weekStartDay${timeSettings.get.weekStartDay()})` }}
              />
            </div>
            <Link route="USER_PROFILE">
              <Gear weight="duotone" tw="text-primary-default" />
            </Link>
          </div>
          <SchemaForm<DateRangeFormatted>
            customRender={customRender}
            fields={fields}
            initial={value}
            onSubmit={(d) => {
              onSubmit(alignWithEndOfDayFromSources(d, useTenantEndOfDay));
              close();
            }}
            SubmitComponent={() => (
              <div tw="pt-3 flex justify-end">
                <Button type="submit" variant={["primary", "sm"]} data-test="submitDateRangeFilter">
                  <Text tKey="common.confirm" />
                </Button>
              </div>
            )}
          />
        </div>
      )}
    >
      <div css={fieldWrapperStyle}>
        {label && <TextLabel>{label}</TextLabel>}
        <Box variant={variant}>
          {value.duration === "custom" && (value.from || value.to) ? (
            <Text
              tKey="dateFormat"
              tw="truncate"
              tValue={{
                date: [getDate(value.from), getDate(value.to)],
                formatString: "PPp_period",
                from: !value.to ? "$t(common.from)" : "",
                to: !value.from ? "$t(common.to)" : "",
              }}
            />
          ) : placeholder && !value.from && !value.to ? (
            <TextPlaceholder tKey={placeholder} />
          ) : (
            <Text>{durationOptions.find((o) => o.value === value.duration)?.label || "-"}</Text>
          )}
          {isClearable && (value.from || value.to) ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onSubmit({ duration: "custom", from: "", to: "" });
              }}
            >
              &times;
            </span>
          ) : null}
        </Box>
      </div>
    </Popover>
  );
};

const alignWithEndOfDayFromSources = (value, useTenantEndOfDay = false, userPriorityOverTenantEndOfDay = true) => {
  if (value.duration === "custom") {
    return value;
  }
  if (userPriorityOverTenantEndOfDay) {
    const endOfDay = timeSettings.get.dayEndTime();
    if (getLocalStorageData(timeSettings.keys.USER_DAY_BREAK_ENABLED)) return applyDayBreak(value, endOfDay);
  }
  const tenantEndOfDay = useTenant.getState().tenant?.endOfDay;
  if (useTenantEndOfDay && tenantEndOfDay) {
    return applyDayBreak(value, tenantEndOfDay);
  } else {
    const endOfDay = timeSettings.get.dayEndTime();
    return applyDayBreak(value, endOfDay);
  }
};

export const useDateRangeFilter = (
  duration: Duration | "empty" = "day",
  options?: Omit<Props, "onSubmit" | "value">,
): [DateRangeFormatted, JSX.Element] => {
  const getDefaultState = () => {
    return {
      duration: duration === "empty" ? "custom" : duration,
      ...(duration === "empty"
        ? { to: "", from: "" }
        : alignWithEndOfDayFromSources(
            getDatesDurationByISO(getDatesByDuration[duration]()),
            options?.useTenantEndOfDay,
          )),
    };
  };
  const [state, setState] = useState<DateRangeFormatted>(getDefaultState);

  const tenantEndOfDay = useTenant((s) => s.tenant?.endOfDay);

  useEffect(() => {
    setState((previous) => (previous.duration === "custom" ? previous : getDefaultState()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantEndOfDay]);

  return [state, <DateFilter key="dateRange" onSubmit={setState} value={state} {...options} />];
};

export const useDateRangeZoned = (range: DateRangeFormatted, zoneId?: string, useTenantEndOfDay = true) =>
  useMemo(() => {
    //  we don't do this for custom duration, only for specified durations
    if (range.duration === "custom" || !zoneId || !range.to) return range;

    const isLastOrCurrentRange = range.duration.startsWith("last") || range.duration.startsWith("current");

    return alignWithEndOfDayFromSources(
      getDatesByDuration[range.duration](
        // last and current are relative to exact current time
        // otherwise its relative to timezone and end time is not relative
        isLastOrCurrentRange
          ? new Date(dateToISOLocal(new Date(), zoneId))
          : new Date(dateToISOLocal(range.to, zoneId)),
      ),
      useTenantEndOfDay,
    );
  }, [range, zoneId, useTenantEndOfDay]);

export const useDateZoned = <T extends string | Date | undefined>(date: T, zoneId?: string) =>
  useMemo(() => (date ? dateToISOLocal(date, zoneId) : date) as T extends undefined ? T : string, [date, zoneId]);

type DateRangeInfo = {
  from?: string;
  to?: string;
  timeZone?: string;
};

export const DateRangeInfo: React.FC<DateRangeInfo> = ({ from, to, timeZone }) => (
  <div tw="flex flex-row items-center gap-1 mb-2">
    <TextLabel tw="p-0" tKey="common.table.selectedDays" tValue={{ colon: true }} />
    <Text
      variant="label"
      tKey="dateFormat"
      tValue={{
        date: [getDate(from), getDate(to)],
        formatString: "PPp_period",
        from: !to ? "$t(common.from)" : "",
        to: !from ? "$t(common.to)" : "",
      }}
    />
    {timeZone ? (
      <Text variant="label" tKey="common.table.inTimeZone" tValue={{ value: timeZone }} />
    ) : (
      <Text variant="label" tKey="common.table.inLocalTime" />
    )}
  </div>
);
